import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Icon, NewTable, useToast } from '@vallourec-ui/vallourec-ui'

import { useTranslation } from 'react-i18next'

import { isAxiosError } from 'axios'

import { Container } from './style'

import { PageProps } from '@/@types/global'

import UserModal from './_components/UserModal'

import { TableWrapper } from '../Flow/_component/DataToResult/style'
import { UserCountProps } from '@/@types/userType'
import { getUsers } from '@/api/get'
import { getUser } from '@/api/post'
import Header from '@/components/Header'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useApp } from '@/stores/AppStore'

const Users: React.FC<PageProps> = ({ title, subtitle }) => {
  const { t } = useTranslation()
  usePageTitle({ title, subtitle })

  const { setIsLoading } = useApp()
  const { toast } = useToast()

  const [users, setUsers] = useState<
    (UserProps & { fullname?: string; isAdminView?: string })[]
  >([])
  const [countUsers, setCountUsers] = useState<number>(0)

  const [currentPage, setCurrentPage] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(10)
  const [searchValue, setSearchValue] = useState<string>('')
  const [tempSearchValue, setTempSearchValue] = useState<string>('')

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [userSelected, setUserSelected] = useState<
    (UserProps & { fullname?: string; isAdminView?: string }) | undefined
  >(undefined)

  const fetchData = async (search?: string) => {
    try {
      setIsLoading(true)

      setUsers([])
      setCountUsers(0)

      const response = await getUsers()
      const data = response?.users

      setUsers(data)
      setCountUsers(data.length)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)
      if (isAxiosError(err)) {
        toast({
          title: 'Error',
          description: err?.message ? err?.message : t<string>('home.error'),
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totalPerPage = useMemo(() => {
    const total = Math.ceil(countUsers / itemsPerPage)
    setTotalPages(total)
    return total
  }, [countUsers, itemsPerPage])

  const canPreviusPage = useMemo(() => {
    if (currentPage === 0) {
      return false
    }
    if (currentPage + 1 === totalPages) {
      return true
    }
    return true
  }, [currentPage, totalPages])

  const canNextPage = useMemo(() => {
    if (currentPage + 1 === totalPages) {
      return false
    }
    return true
  }, [currentPage, totalPages])

  const columns = useMemo(
    () => [
      {
        id: 'pn',
        accessorKey: 'pn',
        header: t<string>('users.table.pn'),
      },
      {
        id: 'network_login',
        accessorKey: 'network_login',
        header: t<string>('users.table.networkLogin'),
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: t<string>('users.table.name'),
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: t<string>('users.table.email'),
      },
      {
        id: 'position',
        accessorKey: 'position',
        header: t<string>('users.table.position'),
      },
      {
        id: 'button',
        accessorKey: 'button',
        header: '',
        // eslint-disable-next-line react/no-unstable-nested-components, @typescript-eslint/no-explicit-any
        cell: (row: any) => (
          <Button
            onClick={() => {
              setUserSelected(row.row.original as UserProps)
              setModalIsOpen(true)
            }}
            key="confirm-button"
            variant="secondary-grey"
          >
            <Icon name="EditLine" />
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, users]
  )

  // TODO - refactor view page (bug of table reder)
  const renderTable = useCallback(() => {
    return users?.length > 0 ? (
      <TableWrapper>
        <NewTable.Root
          key="Table-with-users"
          columnIdWithRowspan="button"
          columns={columns}
          data={users.map((user) => ({
            ...user,
            isAdminView: t<string>(
              `users.table.${user.isAdmin ? 'yes' : 'no'}Option`
            ),
          }))}
          hasPagination
          isBordered
          hasScroll
        />
      </TableWrapper>
    ) : (
      <NewTable.Root key="Table-without-users" data={[]} columns={columns} />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, itemsPerPage, currentPage, canNextPage, canPreviusPage, t])

  const handleOnFilter = async () => {
    setTempSearchValue(searchValue)
    if (currentPage === 0) {
      await fetchData(searchValue)
    } else {
      setCurrentPage(0)
    }
  }

  const handleOnSave = (user: UserProps) => {
    const newUsers = users.map((item) => ({
      ...item,
      ...(item.id === user.id
        ? {
            ...user,
            fullname: `${user.name} ${user.lastName}`.trim(),
            isAdminView: t<string>(
              `users.table.${user.isAdmin ? 'yes' : 'no'}Option`
            ),
          }
        : {}),
    }))

    setUsers(newUsers)
  }

  return (
    <Container>
      {renderTable()}
      <UserModal
        isOpen={modalIsOpen}
        userData={userSelected ?? ({} as UserProps)}
        title={t('users.userModal.title', 'Dados de Usuário')}
        setIsOpen={setModalIsOpen}
        onSave={handleOnSave}
        onOutsideClick={() => {
          setModalIsOpen(false)
          setUserSelected(undefined)
        }}
      />
    </Container>
  )
}

export default Users
